import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const faqs = [
  {
    question: 'How do I create an account?',
    answer:
      'Open Docket and tap "Sign Up". Enter your email address and choose a password. You\'ll receive a verification code by email — enter it to confirm your account and you\'re ready to go.',
  },
  {
    question: 'I didn\'t receive my verification code.',
    answer:
      'Check your spam or junk folder first. Verification emails are sent from a no-reply AWS address. If you still can\'t find it, tap "Resend code" on the verification screen. If the problem persists, contact us at support@wolftechnologies.co.uk.',
  },
  {
    question: 'I\'ve forgotten my password.',
    answer:
      'Tap "Forgot password?" on the sign-in screen. Enter your email address and you\'ll receive a reset code. Follow the prompts to set a new password.',
  },
  {
    question: 'What are the GTD views in Docket?',
    answer:
      'Docket organises tasks into purpose-built views: Inbox (capture point for new tasks), Today (your daily focus list), Upcoming (future tasks in calendar view), Anytime (available actions with no scheduled date), Someday (ideas for the future), Logbook (completed tasks), and Bin (deleted tasks, removed after 30 days).',
  },
  {
    question: 'What\'s the difference between a due date and a deadline?',
    answer:
      'A due date is when you plan to start or work on a task — it appears in your Today view on that date. A deadline is a hard cutoff — the latest date by which the task must be completed. You can set both on a single task.',
  },
  {
    question: 'What\'s the difference between Projects and Areas?',
    answer:
      'Projects are outcomes that require multiple tasks to complete (e.g. "Plan holiday"). A project is complete when all its tasks are done. Areas of Responsibility are ongoing spheres of your life (e.g. "Health", "Finance") that contain projects and tasks but are never "completed". Areas are a premium feature.',
  },
  {
    question: 'What\'s included in the free plan?',
    answer:
      'Full access to Inbox, Today, Upcoming, Anytime, Someday, and Logbook views. You can create up to 5 active projects, use tags, and manage tasks with due dates and checklists.',
  },
  {
    question: 'What do I get with Premium?',
    answer:
      'Premium unlocks Areas of Responsibility, unlimited projects, repeating tasks, and advanced search. Plans: $3.99/month, $29.99/year (save 37%), or $79.99 lifetime. All new accounts get a 14-day free trial — no payment details required.',
  },
  {
    question: 'How do repeating tasks work?',
    answer:
      'With a premium subscription, you can set tasks to repeat on a schedule (daily, weekly, monthly, or custom). When you complete a repeating task, a new instance is automatically created with the next due date.',
  },
  {
    question: 'How do I manage or cancel my subscription?',
    answer:
      'On the web (Stripe): go to Settings > Manage Subscription to open the Stripe Customer Portal where you can update payment, change plan, or cancel. On iOS (Apple): go to device Settings > your name > Subscriptions > Docket. After cancellation, you retain premium access until the end of your billing period, then revert to free. Your data is never deleted.',
  },
  {
    question: 'Can I get a refund?',
    answer:
      'For web (Stripe) purchases, contact us at support@wolftechnologies.co.uk within 14 days for a refund. For iOS (Apple) purchases, visit reportaproblem.apple.com to request a refund from Apple.',
  },
  {
    question: 'Is my data synced across devices?',
    answer:
      'Yes. Your tasks, projects, and areas are stored in the cloud (AWS) and available on any device where you sign in. Changes sync automatically when connected to the internet.',
  },
  {
    question: 'Does Docket work offline?',
    answer:
      'Docket is a Progressive Web App (PWA) that can be installed and launched offline. However, an active internet connection is required to sync changes. Tasks created or modified offline will sync the next time you connect.',
  },
  {
    question: 'How do I delete my account and data?',
    answer:
      'Contact us at support@wolftechnologies.co.uk to request full account deletion. All your data (tasks, projects, areas, tags, and profile) will be permanently deleted within 30 days.',
  },
  {
    question: 'The app won\'t load or shows a blank screen.',
    answer:
      'Clear your browser cache and reload the page. Ensure you\'re using an up-to-date browser (Chrome, Safari, Firefox, or Edge). Check your internet connection. If using the installed PWA, try uninstalling and reinstalling it. If the issue persists, contact us with your browser and device details.',
  },
];

const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div className={`faq__item ${isOpen ? 'faq__item--open' : ''}`} onClick={onClick}>
    <div className="faq__question">
      <h3>{question}</h3>
      <svg
        className="faq__chevron"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M6 9l6 6 6-6" />
      </svg>
    </div>
    {isOpen && (
      <div className="faq__answer">
        <p>{answer}</p>
      </div>
    )}
  </div>
);

const DocketSupport = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="legal-page">
      <div className="legal-page__header">
        <div className="container">
          <Link to="/" className="legal-page__back">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" />
              <path d="M12 19l-7-7 7-7" />
            </svg>
            Back to Home
          </Link>
          <span className="section__tag">Support</span>
          <h1 className="legal-page__title">Docket Support</h1>
          <p className="legal-page__subtitle">
            Get help with Docket - Task Manager by Wolf Technologies Ltd
          </p>
        </div>
      </div>

      <div className="container">
        <div className="legal-page__content">

          {/* Contact Section */}
          <section className="support-contact">
            <div className="support-contact__card">
              <div className="support-contact__icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </div>
              <div>
                <h3>Need help?</h3>
                <p>
                  Contact us at{' '}
                  <a href="mailto:support@wolftechnologies.co.uk">support@wolftechnologies.co.uk</a>
                </p>
                <p className="support-contact__response">
                  We aim to respond to all enquiries within 48 hours.
                </p>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="legal-section">
            <h2>Frequently Asked Questions</h2>
            <div className="faq__list">
              {faqs.map((faq, index) => (
                <FAQItem
                  key={index}
                  question={faq.question}
                  answer={faq.answer}
                  isOpen={openIndex === index}
                  onClick={() => toggleFAQ(index)}
                />
              ))}
            </div>
          </section>

          {/* App Info */}
          <section className="legal-section">
            <h2>App Information</h2>
            <div className="support-info__grid">
              <div className="support-info__item">
                <span className="support-info__label">App</span>
                <span className="support-info__value">Docket - Task Manager</span>
              </div>
              <div className="support-info__item">
                <span className="support-info__label">Version</span>
                <span className="support-info__value">1.0</span>
              </div>
              <div className="support-info__item">
                <span className="support-info__label">Developer</span>
                <span className="support-info__value">Wolf Technologies Ltd</span>
              </div>
              <div className="support-info__item">
                <span className="support-info__label">Platform</span>
                <span className="support-info__value">Web (PWA), iOS</span>
              </div>
            </div>
          </section>

          {/* Links */}
          <section className="legal-section">
            <h2>Useful Links</h2>
            <div className="support-links">
              <Link to="/docket/privacy" className="support-links__item">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0110 0v4" />
                </svg>
                Privacy Policy
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14" />
                  <path d="M12 5l7 7-7 7" />
                </svg>
              </Link>
              <a href="/" className="support-links__item">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                  <polyline points="9 22 9 12 15 12 15 22" />
                </svg>
                WolfTechnologies Home
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14" />
                  <path d="M12 5l7 7-7 7" />
                </svg>
              </a>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default DocketSupport;
